import { Breadcrumb } from 'components/authorable/shared/navigation/Breadcrumb/Breadcrumb';
import styles from './ProductHero.module.scss';
import { BazaarVoiceRating } from 'components/helpers/BazaarVoice';
import { ActionItem, ActionToolbar } from 'components/helpers/ActionToolbar/ActionToolbar';
import EnhancedNextImage from 'components/helpers/Atoms/EnhancedNextImage/EnhancedNextImage';
import React from 'react';
import PlainTextWrapper from 'components/helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import useDictionary from 'lib/hooks/useDictionary';
import { ProductHeroDecor } from '../ProductHeroDecor/ProductHeroDecor';
import { clsx } from 'clsx';
import { useMetaDataTag } from 'lib/hooks/useMetaDataTag';
import cardStyles from 'components/authorable/shared/card-listing/Card.module.scss';
import { ProductResponse } from 'lib/services/models/content-response';
import LinkWrapper from 'components/helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { LinkFieldValue } from '@sitecore-content-sdk/nextjs';
import { CtaComponentClass } from 'lib/hooks/theming/useCtaComponentClass';
import { normalizeSymbols } from 'lib/utils/normalize-symbols';
import EnhancedRichText from 'components/helpers/Atoms/EnhancedRichText/EnhancedRichText';
import { OneTrustFilter } from 'components/helpers/OneTrust';
import { usePearCommerceIsEnabled } from 'lib/hooks/usePearCommerceIsEnabled';

const renderProductTitle = (title: string) => normalizeSymbols(title);

export interface ProductHeroProps {
  caseCount?: string | null;
  contentId?: string;
  ratingAndReviewContentId?: string;
  heroActionToolbarIdentifier?: string;
  heroActionToolbarSettings?: ActionItem[];
  imagesToDisplay?: { imageUrl: string; imageAlt?: string }[];
  metadataIds?: string[];
  product?: ProductResponse;
  productCode?: string | null;
  title: string;
  description?: string;
  pageUrl?: string;
  primaryCta?: LinkFieldValue;
  createdDate?: string;
  secondaryCta?: LinkFieldValue;
  urlBreadcrumbs?: Array<{ title: string; url: string }>;
  pdpHeroSettings?: {
    hidePdpHero?: boolean;
    hidePdpHeroPearCommerceApp?: boolean;
    hidePdpHeroRatingsAndReviews?: boolean;
    showCaseCount?: boolean;
    showPrimaryCta?: boolean;
    showProductCode?: boolean;
    showSecondaryCta?: boolean;
    verticallyAlignTitle?: boolean;
    heroVariationInternational?: boolean;
  };
}

export function ProductHero(props: ProductHeroProps) {
  const { getDictionaryValue } = useDictionary();
  const { isEnabled: isPearCommerceEnabled } = usePearCommerceIsEnabled();
  const {
    labelName: metaDataTagLabel,
    metaDataTagAppearance,
    metaDataTagColor,
  } = useMetaDataTag(props.metadataIds);
  const imageAriaLabelDictionaryKey =
    'Components.Product.ProductDescription.ProductHeroImagesAriaLabel';
  const imageAriaLabelPrefixValue = getDictionaryValue(imageAriaLabelDictionaryKey);
  const caseCountDictionaryKey = 'Components.Product.ProductHero.CaseCount';
  const productCodeDictionaryKey = 'Components.Product.ProductHero.ProductCode';
  const caseCountLabel = getDictionaryValue(caseCountDictionaryKey);
  const productCodeLabel = getDictionaryValue(productCodeDictionaryKey);
  const imageAriaLabelPrefix =
    imageAriaLabelPrefixValue && imageAriaLabelPrefixValue !== imageAriaLabelDictionaryKey
      ? imageAriaLabelPrefixValue
      : 'Product image';
  const images = props.imagesToDisplay || [];
  const [selectedIdx, setSelectedIdx] = React.useState(0);
  const {
    caseCount,
    title,
    heroActionToolbarSettings,
    product,
    productCode,
    urlBreadcrumbs,
    heroActionToolbarIdentifier,
  } = props;

  const {
    hidePdpHeroPearCommerceApp,
    hidePdpHeroRatingsAndReviews,
    showCaseCount,
    showPrimaryCta,
    showProductCode,
    showSecondaryCta,
    verticallyAlignTitle,
    heroVariationInternational,
  } = props.pdpHeroSettings || {};
  if (props.pdpHeroSettings?.hidePdpHero) {
    return <></>;
  }

  const resolvedCaseCount =
    caseCount ?? product?.CaseCount ?? product?.ProductReferenceOnly?.caseCount;
  const resolvedProductCode =
    productCode ??
    product?.ProductId ??
    (product?.ProductReferenceOnly?.productId
      ? parseInt(product.ProductReferenceOnly.productId, 10).toString()
      : null);

  const shouldShowCaseCount = Boolean(showCaseCount && resolvedCaseCount);
  const shouldShowProductCode = Boolean(showProductCode && resolvedProductCode);
  const shouldShowProductInfo = shouldShowCaseCount || shouldShowProductCode;
  const hasResolvedCaseCountLabel = caseCountLabel && caseCountLabel !== caseCountDictionaryKey;
  const hasResolvedProductCodeLabel =
    productCodeLabel && productCodeLabel !== productCodeDictionaryKey;
  const shouldShowPrimaryCta = Boolean(showPrimaryCta && props.primaryCta?.href);
  const shouldShowSecondaryCta = Boolean(showSecondaryCta && props.secondaryCta?.href);
  const shouldShowCtas = shouldShowPrimaryCta || shouldShowSecondaryCta;
  const breadcrumbData = urlBreadcrumbs?.map((breadcrumb) => ({
    url: breadcrumb.url,
    title: <PlainTextWrapper field={{ value: breadcrumb.title }} />,
  }));

  return (
    <>
      <div
        className={clsx(styles.productHeroWrapper, {
          [styles.verticallyAligned]: verticallyAlignTitle,
        })}
      >
        <div className={styles.productHeroDetails}>
          <Breadcrumb ancestors={breadcrumbData || []} isBreadcrumbForHero={true} />
          <div
            className={clsx(styles.productHeroTitleWrapper, {
              [styles.productHeroTitleWrapperPadding]:
                !hidePdpHeroPearCommerceApp && isPearCommerceEnabled,
            })}
          >
            {metaDataTagLabel && (
              <div className={styles.productHeroMetaDataTagWrapper}>
                <div
                  className={clsx(
                    cardStyles.cardLabel,
                    cardStyles[`cardLabel--${metaDataTagAppearance.toLowerCase()}`],
                    cardStyles[`cardLabel--${metaDataTagColor === 'Type B' ? 'colorB' : 'colorA'}`]
                  )}
                >
                  {metaDataTagLabel}
                </div>
              </div>
            )}
            <h1 className={styles.productHeroTitle}>{renderProductTitle(title)}</h1>
            {heroVariationInternational && props.description && (
              <div className={styles.productHeroDescription}>
                <EnhancedRichText value={props.description} />
              </div>
            )}
            {shouldShowProductInfo && (
              <div className={styles.productHeroInfo}>
                {shouldShowCaseCount && hasResolvedCaseCountLabel && (
                  <div className={styles.productHeroInfoItem}>
                    <span className={styles.productHeroInfoLabel}>{caseCountLabel}</span>
                    <span className={styles.productHeroInfoValue}>{resolvedCaseCount}</span>
                  </div>
                )}
                {shouldShowCaseCount && shouldShowProductCode && (
                  <span aria-hidden="true" className={styles.productHeroInfoSeparator}>
                    |
                  </span>
                )}
                {shouldShowProductCode && hasResolvedProductCodeLabel && (
                  <div className={styles.productHeroInfoItem}>
                    <span className={styles.productHeroInfoLabel}>{productCodeLabel}</span>
                    <span className={styles.productHeroInfoValue}>{resolvedProductCode}</span>
                  </div>
                )}
              </div>
            )}
            {!hidePdpHeroRatingsAndReviews && !heroVariationInternational && (
              <OneTrustFilter
                optanonGroups={['2', '3', '4']}
                enablePlaceholder={false}
                filteredComponent={
                  <BazaarVoiceRating
                    ratingAndReviewContentId={props.ratingAndReviewContentId}
                    showType="rating_summary"
                  />
                }
              />
            )}
          </div>
        </div>
        <div
          className={`${styles.productHeroImageContainer} ${images.length === 1 ? styles.singleImage : ''}`}
        >
          {images[selectedIdx] && (
            <EnhancedNextImage
              src={images[selectedIdx].imageUrl}
              alt={images[selectedIdx].imageAlt || title}
              width={480}
              height={480}
              className={styles.productHeroImage}
              priority
            />
          )}
          {images.length > 1 && (
            <div className={styles.productHeroThumbnailGrid}>
              <ul className={styles.productHeroThumbnailList}>
                {images.map((asset: { imageUrl: string; imageAlt?: string }, idx: number) => (
                  <li className={styles.productHeroThumbnailListItem} key={idx}>
                    <button
                      key={idx}
                      type="button"
                      className={`${styles.productHeroThumbnailButton}`}
                      onClick={() => {
                        setSelectedIdx(idx);
                      }}
                      tabIndex={0}
                      aria-label={`${asset.imageAlt || title} ${imageAriaLabelPrefix} ${idx + 1}`}
                    >
                      <EnhancedNextImage
                        src={asset.imageUrl}
                        alt={asset.imageAlt || title}
                        width={64}
                        height={64}
                        className={`${styles.productHeroThumbnail} ${selectedIdx === idx ? styles.activeThumbnail : ''}`}
                      />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {shouldShowCtas && (
          <div className={styles.productHeroCtaContainer}>
            {shouldShowPrimaryCta && (
              <LinkWrapper
                field={props.primaryCta}
                ctaComponentClass={'component-hero-product-color-cta-1-color' as CtaComponentClass}
                ctaStyle="filled"
                suppressNewTabIcon={true}
                contentResultAnalytics={{
                  contentListName: title,
                }}
              />
            )}
            {shouldShowSecondaryCta && (
              <LinkWrapper
                field={props.secondaryCta}
                ctaComponentClass={'component-hero-product-color-cta-2-color' as CtaComponentClass}
                ctaStyle="outline"
                suppressNewTabIcon={true}
                contentResultAnalytics={{
                  contentListName: title,
                }}
              />
            )}
          </div>
        )}

        <div className={styles.productHeroDetailsBottom}>
          {!hidePdpHeroPearCommerceApp && isPearCommerceEnabled && (
            <OneTrustFilter
              optanonGroups={['4']}
              enablePlaceholder={true}
              placeholderFeatureType="retailerlist"
              filteredComponent={
                <div
                  id="pear-pdp-target"
                  className={styles.pearWidgetContainer}
                  tabIndex={-1}
                  aria-label="Shop products"
                />
              }
            />
          )}
          <div className={styles.actionToolBarContainer}>
            <ActionToolbar
              actions={heroActionToolbarSettings}
              toolbarIdentifier={heroActionToolbarIdentifier}
              variant="horizontal"
              className={styles.centerAlignActionToolbar}
            />
          </div>
        </div>
      </div>
      <div className={styles.productHeroDecorWrapper}>
        <ProductHeroDecor />
      </div>
    </>
  );
}
